import { useState, useEffect, useRef } from "react";
import { remapPlayerAvailability } from "../utils/migrateAvailability";
import { defaultCoachColor } from "../utils/coachColors";
import { applyHouseholds } from "../utils/households";
import { addToWaitlist, removeFromWaitlist, moveInWaitlist } from "../utils/waitlist";
import { resolvePrimaryGroupId } from "../utils/membership";
import { apiFetch } from "../utils/auth";
import { renameLegacyCourts } from "../utils/tiers";

// Writes a waitlist onto a group, dropping the field entirely when the queue
// is empty so untouched groups stay byte-identical in kac_data.json.
function withWaitlist(group, next) {
  if (next && next.length > 0) return { ...group, waitlist: next };
  const { waitlist, ...rest } = group;
  return rest;
}

const STORAGE_KEY = "kac-tennis-state";

// The schedule comes back with the revision it's at. Every save names the
// revision it was built on, and the server refuses one built on anything
// older — otherwise a tab left open from yesterday puts back everything it
// never saw, which is how members added in one browser were deleted by a save
// from another.
async function fetchFromServer() {
  try {
    const res = await apiFetch("");
    if (!res.ok) return null;
    return { data: await res.json(), revision: res.headers.get("X-KAC-Revision") || "" };
  } catch {
    // Includes an expired session: apiFetch has already signalled that, and
    // the app is on its way back to the sign-in screen.
    return null;
  }
}

// Resolves to the new revision, or throws a SaveError saying why not.
async function saveToServer(state, revision) {
  let res;
  try {
    res = await apiFetch("", {
      method: "POST",
      headers: { "X-KAC-Revision": revision || "" },
      body: JSON.stringify(state),
    });
  } catch (err) {
    if (err?.name === "SessionExpiredError") throw err;
    throw new SaveError("unreachable");
  }
  if (res.status === 409) throw new SaveError("conflict");
  if (!res.ok) throw new SaveError("failed");
  return res.headers.get("X-KAC-Revision") || "";
}

class SaveError extends Error {
  constructor(kind) {
    super(kind);
    this.kind = kind;
  }
}

function withCurrentCourts(data) {
  return { ...data, groups: renameLegacyCourts(data.groups) };
}

function loadFromLocalStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
}

export function useKACState() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);
  const revision = useRef("");
  // Saves run one at a time, each taking the revision the one before it got
  // back — two overlapping saves from this tab would otherwise refuse each
  // other.
  const saveChain = useRef(Promise.resolve());
  // Why saving has stopped: "conflict" (someone else saved first), "failed" or
  // "unreachable". Once set, nothing more is sent until the page is reloaded,
  // so edits made on a copy that can't be saved can't be half-saved later.
  const [saveProblem, setSaveProblem] = useState(null);
  const saveBlocked = useRef(false);

  // Load from server on mount; fall back to localStorage if server has nothing
  useEffect(() => {
    fetchFromServer().then(loaded => {
      if (loaded?.data?.players) {
        revision.current = loaded.revision;
        setState(withCurrentCourts(loaded.data));
      } else {
        const local = loadFromLocalStorage();
        if (local) setState(withCurrentCourts(local));
      }
      setLoading(false);
    });
  }, []);

  // Resolves true once `next` is on the server, false if it never got there.
  function queueSave(next) {
    saveChain.current = saveChain.current.then(async () => {
      if (saveBlocked.current) return false;
      try {
        revision.current = await saveToServer(next, revision.current);
        return true;
      } catch (err) {
        if (err?.name !== "SessionExpiredError") {
          saveBlocked.current = true;
          setSaveProblem(err?.kind || "failed");
        }
        return false;
      }
    });
    return saveChain.current;
  }

  // The latest state, for the few edits that must be saved before the caller
  // moves on, and the state such an edit already sent, so the effect below
  // doesn't send it a second time.
  const latest = useRef(state);
  latest.current = state;
  const savedDirectly = useRef(null);

  // Save to server + localStorage whenever state changes
  useEffect(() => {
    if (!state || loading || saveBlocked.current) return;
    if (state === savedDirectly.current) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => queueSave(state), 1000);
    return () => clearTimeout(saveTimer.current);
  }, [state, loading]);

  function loadData(data) {
    setState({
      players: data.players,
      groups: renameLegacyCourts(data.groups),
      coaches: data.coaches || [],
      terms: data.terms || [],
      activeTermName: data.activeTermName || "",
    });
  }

  function addCoach(coach) {
    setState(prev => {
      const maxId = (prev.coaches || []).reduce((max, c) => {
        const num = parseInt(String(c.id).replace("C", ""), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
      const newCoach = {
        id: `C${String(maxId + 1).padStart(3, "0")}`,
        name: coach.name,
        maxHours: coach.maxHours ?? null,
        notes: coach.notes || "",
        email: coach.email || "",
        phone: coach.phone || "",
        availability: coach.availability ?? null,
        // Fall back to a palette color keyed off how many coaches already
        // exist, so a fresh coach always lands on a distinct-looking tag.
        color: coach.color || defaultCoachColor((prev.coaches || []).length),
      };
      return { ...prev, coaches: [...(prev.coaches || []), newCoach] };
    });
  }

  function updateCoach(coachId, updates) {
    setState(prev => ({
      ...prev,
      coaches: (prev.coaches || []).map(c =>
        c.id === coachId ? { ...c, ...updates } : c
      ),
    }));
  }

  // Removing a coach also clears them from any group they were assigned to,
  // so no group is left pointing at a coach that no longer exists.
  function deleteCoach(coachId) {
    setState(prev => ({
      ...prev,
      coaches: (prev.coaches || []).filter(c => c.id !== coachId),
      groups: prev.groups.map(g =>
        g.coachId === coachId ? { ...g, coachId: null } : g
      ),
    }));
  }

  function addPlayers(newPlayers) {
    setState(prev => ({
      ...prev,
      players: [...prev.players, ...newPlayers],
    }));
  }

  // Adds players and saves straight away, resolving true only once they're on
  // the server. Approving a sign-up marks the form approved on the server too,
  // and that mustn't happen for a member whose save was refused — the form
  // would read approved while the member was never added, with nothing on
  // screen to say so.
  function addPlayersAndSave(newPlayers) {
    const prev = latest.current;
    if (!prev) return Promise.resolve(false);
    const next = { ...prev, players: [...prev.players, ...newPlayers] };
    latest.current = next;
    savedDirectly.current = next;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setState(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return queueSave(next);
  }

  // Takes on a document the server already holds, at the revision it gave for
  // it — what an undo from the Activity view hands back. Marked as already
  // saved, so the effect above doesn't post it straight back: that save would
  // name the revision this tab had before the undo and be refused, leaving the
  // app stuck behind the conflict banner over a change it made itself.
  function adoptFromServer(data, atRevision) {
    if (!data?.players) return;
    const next = withCurrentCourts({
      players: data.players,
      groups: data.groups || [],
      coaches: data.coaches || [],
      terms: data.terms || [],
      activeTermName: data.activeTermName || "",
    });
    if (atRevision) revision.current = atRevision;
    latest.current = next;
    savedDirectly.current = next;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setState(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  // Stamps householdId onto players from an uploaded member import file, so
  // either parent's email pulls up the whole family in "My Schedule".
  // Returns the link report for the caller to surface.
  function linkHouseholds(households) {
    if (!state?.players) return { linked: 0, households: 0, unmatched: [] };
    // Computed from the current snapshot (an import is a single deliberate
    // action) so the caller gets the report back synchronously.
    const result = applyHouseholds(state.players, households);
    setState(prev => ({ ...prev, players: result.players }));
    return { linked: result.linked, households: result.households, unmatched: result.unmatched };
  }

  function resetData() {
    localStorage.removeItem(STORAGE_KEY);
    setState(null);
    queueSave(null);
  }

  function removeFromGroup(playerId, groupId) {
    setState(prev => {
      const groups = prev.groups.map(g =>
        g.id === groupId
          ? { ...g, playerIds: g.playerIds.filter(id => id !== playerId) }
          : g
      );
      // Someone in two clinics who leaves one is still placed in the other,
      // so the primary is re-resolved rather than blanked.
      const players = prev.players.map(p =>
        p.id === playerId ? { ...p, groupId: resolvePrimaryGroupId(p, groups) } : p
      );
      return { ...prev, players, groups };
    });
  }

  function deleteGroup(groupId) {
    setState(prev => {
      const groups = prev.groups.filter(g => g.id !== groupId);
      const players = prev.players.map(p =>
        p.groupId === groupId ? { ...p, groupId: resolvePrimaryGroupId(p, groups) } : p
      );
      return { ...prev, players, groups };
    });
  }

  function createGroup(groupData) {
    setState(prev => {
      const maxId = prev.groups.reduce((max, g) => {
        const num = parseInt(g.id.replace("G", ""), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
      const newId = groupData.id || `G${String(maxId + 1).padStart(3, "0")}`;
      const courtType = groupData.courtType || (groupData.court?.startsWith("Pickleball") ? "Pickleball" : "Tennis");
      // Weekend sessions (open play / privates) set their own duration and can
      // carry a coach + members at creation; the Pickleball=1h rule only
      // applies to instruction groups.
      const isSession = groupData.kind === "private" || groupData.kind === "openplay";
      const newGroup = {
        id: newId,
        tier: groupData.tier,
        courtType,
        day: groupData.day || "",
        startTime: groupData.startTime || "",
        duration: (courtType === "Pickleball" && !isSession) ? 1 : (groupData.duration || 1),
        court: groupData.court || "",
        playerIds: groupData.playerIds || [],
        notes: groupData.notes || "",
        ...(groupData.kind ? { kind: groupData.kind } : {}),
        ...(groupData.coachId ? { coachId: groupData.coachId } : {}),
      };
      return { ...prev, groups: [...prev.groups, newGroup] };
    });
  }

  function updatePlayer(playerId, updates) {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p =>
        p.id === playerId ? { ...p, ...updates } : p
      ),
    }));
  }

  function remapAvailability() {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p => ({ ...p, availability: remapPlayerAvailability(p) })),
    }));
  }

  // --- Waitlists ---------------------------------------------------------
  // Entries live on the group and never touch player.groupId, so waiting for
  // a group is independent of being in one. See utils/waitlist.js.
  function waitlistAdd(groupId, playerId) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, addToWaitlist(g, playerId)) : g
      ),
    }));
  }

  function waitlistRemove(groupId, playerId) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, removeFromWaitlist(g, playerId)) : g
      ),
    }));
  }

  function waitlistMove(groupId, playerId, delta) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, moveInWaitlist(g, playerId, delta)) : g
      ),
    }));
  }

  function updateGroup(groupId, updates) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? { ...g, ...updates } : g
      ),
    }));
  }

  function movePlayer(playerId, fromGroupId, toGroupId) {
    setState(prev => {
      const players = prev.players.map(p => {
        if (p.id !== playerId) return p;
        const targetGroup = prev.groups.find(g => g.id === toGroupId);
        // Being placed ends both "waiting for next term" and "can't make any
        // clinic" — neither can be true of someone in a group, and leaving a
        // flag set would misfile them in the roster next time they come out
        // of one.
        const { deferred, noFit, ...rest } = p;
        // Only the primary membership follows the move. Shifting a member's
        // second clinic elsewhere leaves their first one — and the tier that
        // came with it — exactly where it was.
        const movingPrimary = !p.groupId || p.groupId === fromGroupId;
        if (!movingPrimary) return rest;
        return { ...rest, groupId: toGroupId, tier: targetGroup ? targetGroup.tier : p.tier };
      });
      const groups = prev.groups.map(g => {
        if (g.id === fromGroupId && fromGroupId !== toGroupId)
          return { ...g, playerIds: g.playerIds.filter(id => id !== playerId) };
        if (g.id === toGroupId) {
          // Being placed satisfies the wait, so the entry goes with it —
          // otherwise someone would sit in a group and queue for it at once.
          const joined = g.playerIds.includes(playerId)
            ? g
            : { ...g, playerIds: [...g.playerIds, playerId] };
          return withWaitlist(joined, removeFromWaitlist(joined, playerId));
        }
        return g;
      });
      return { ...prev, players, groups };
    });
  }

  // Enrol someone in a clinic *as well as* the one(s) they're already in:
  // adds them to the group's roster without disturbing player.groupId, so
  // their first clinic — and the tier it set — stays put. When they had no
  // clinic at all this is just a placement, and the group becomes primary.
  function addToGroup(playerId, toGroupId) {
    setState(prev => {
      const groups = prev.groups.map(g => {
        if (g.id !== toGroupId) return g;
        const joined = g.playerIds.includes(playerId)
          ? g
          : { ...g, playerIds: [...g.playerIds, playerId] };
        return withWaitlist(joined, removeFromWaitlist(joined, playerId));
      });
      const targetGroup = groups.find(g => g.id === toGroupId);
      const players = prev.players.map(p => {
        if (p.id !== playerId) return p;
        const { deferred, noFit, ...rest } = p;
        if (rest.groupId) return rest;
        return { ...rest, groupId: toGroupId, tier: targetGroup ? targetGroup.tier : rest.tier };
      });
      return { ...prev, players, groups };
    });
  }

  // --- Terms (seasons) ---------------------------------------------------
  // The live schedule always stays at the top level (players/groups/coaches)
  // so the member lookup and deploy backup keep working unchanged. Saved
  // terms are independent deep-copy snapshots kept alongside in `terms`.
  function makeTermId() {
    return `T${Date.now()}${Math.floor(Math.random() * 1000)}`;
  }

  function snapshotOf(prev, name, id) {
    return {
      id: id || makeTermId(),
      name,
      savedAt: new Date().toISOString(),
      players: JSON.parse(JSON.stringify(prev.players || [])),
      groups: JSON.parse(JSON.stringify(prev.groups || [])),
      coaches: JSON.parse(JSON.stringify(prev.coaches || [])),
    };
  }

  // Insert or replace (by name) a snapshot of the current live state.
  function upsertSnapshot(terms, prev, name) {
    const idx = terms.findIndex(t => t.name === name);
    if (idx >= 0) terms[idx] = snapshotOf(prev, name, terms[idx].id);
    else terms.push(snapshotOf(prev, name));
    return terms;
  }

  // Save the current live schedule as a named term (updating it if the name
  // already exists) and mark it active.
  function saveTerm(name) {
    setState(prev => {
      const terms = upsertSnapshot([...(prev.terms || [])], prev, name);
      return { ...prev, terms, activeTermName: name };
    });
  }

  // Roll over to a new term as a clone of the current one. The current term is
  // preserved as a snapshot; the live state stays identical but is relabeled
  // to the new term, which you then edit going forward.
  function startNewTerm(newName) {
    setState(prev => {
      let terms = [...(prev.terms || [])];
      if (prev.activeTermName) terms = upsertSnapshot(terms, prev, prev.activeTermName);
      terms = upsertSnapshot(terms, prev, newName);
      return { ...prev, terms, activeTermName: newName };
    });
  }

  // Make a saved term the live schedule. The current term is auto-snapshotted
  // first so no unsaved edits are lost.
  function switchToTerm(termId) {
    setState(prev => {
      let terms = [...(prev.terms || [])];
      const target = terms.find(t => t.id === termId);
      if (!target) return prev;
      if (prev.activeTermName) terms = upsertSnapshot(terms, prev, prev.activeTermName);
      return {
        ...prev,
        players: JSON.parse(JSON.stringify(target.players || [])),
        groups: JSON.parse(JSON.stringify(target.groups || [])),
        coaches: JSON.parse(JSON.stringify(target.coaches || [])),
        activeTermName: target.name,
        terms,
      };
    });
  }

  function deleteTerm(termId) {
    setState(prev => ({ ...prev, terms: (prev.terms || []).filter(t => t.id !== termId) }));
  }

  function renameTerm(termId, name) {
    setState(prev => {
      const target = (prev.terms || []).find(t => t.id === termId);
      const terms = (prev.terms || []).map(t => (t.id === termId ? { ...t, name } : t));
      const activeTermName = target && target.name === prev.activeTermName ? name : prev.activeTermName;
      return { ...prev, terms, activeTermName };
    });
  }

  function duplicateTerm(termId, newName) {
    setState(prev => {
      const terms = [...(prev.terms || [])];
      const src = terms.find(t => t.id === termId);
      if (!src) return prev;
      terms.push({
        id: makeTermId(),
        name: newName,
        savedAt: new Date().toISOString(),
        players: JSON.parse(JSON.stringify(src.players || [])),
        groups: JSON.parse(JSON.stringify(src.groups || [])),
        coaches: JSON.parse(JSON.stringify(src.coaches || [])),
      });
      return { ...prev, terms };
    });
  }

  return {
    players: state?.players ?? null,
    groups: state?.groups ?? null,
    coaches: state?.coaches ?? [],
    terms: state?.terms ?? [],
    activeTermName: state?.activeTermName ?? "",
    loading,
    saveProblem,
    movePlayer,
    addToGroup,
    updatePlayer,
    waitlistAdd,
    waitlistRemove,
    waitlistMove,
    updateGroup,
    remapAvailability,
    removeFromGroup,
    createGroup,
    deleteGroup,
    loadData,
    adoptFromServer,
    addPlayers,
    addPlayersAndSave,
    linkHouseholds,
    addCoach,
    updateCoach,
    deleteCoach,
    resetData,
    saveTerm,
    startNewTerm,
    switchToTerm,
    deleteTerm,
    renameTerm,
    duplicateTerm,
  };
}
